/**
 * Close an open popover when the user clicks outside it or presses Escape.
 *
 * Written once because it was already written twice, identically: `CommunitySelect`
 * and `MemberSelect` each carried a `rootEl` binding, a `<svelte:window onclick>`
 * that tested `rootEl.contains(e.target)`, and an Escape handler beside it. Both now
 * use this, so the third popover (`RosterAccessBanner`'s instructions shelf) is not a
 * third copy.
 *
 *     <div use:dismissable={{ isOpen: () => open, close: () => (open = false) }}>
 *       <button onclick={() => (open = !open)}>…</button>
 *       {#if open}<div class="absolute …">…</div>{/if}
 *     </div>
 *
 * Two details that are load-bearing:
 *
 *  * `isOpen` is a GETTER, not a boolean. The action is attached once, for the life
 *    of the node, and reads the current state on each event; taking a boolean would
 *    mean re-running the action on every toggle, tearing the listeners down and
 *    building them back up to learn something it can just ask for.
 *  * The trigger must live INSIDE the node this is used on. The click that opens the
 *    popover also reaches `window`, and it is `contains()` returning true that stops
 *    that same click from immediately closing it again.
 */
export function dismissable(
  node: HTMLElement,
  opts: { isOpen: () => boolean; close: () => void },
): { destroy(): void } {
  const onClick = (e: MouseEvent): void => {
    if (!opts.isOpen()) return;
    if (!(e.target instanceof Node)) return;
    if (!node.contains(e.target)) opts.close();
  };
  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && opts.isOpen()) opts.close();
  };
  window.addEventListener('click', onClick);
  window.addEventListener('keydown', onKeydown);
  return {
    destroy(): void {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKeydown);
    },
  };
}
